import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface GuideTooltipProps {
  term: string;
  children: React.ReactNode;
  side?: 'top' | 'bottom';
}

export const GuideTooltip: React.FC<GuideTooltipProps> = ({ term, children, side = 'top' }) => {
  const { language } = useLanguage();

  // Combined definitions and titles for lookup
  const glossary: Record<string, { title: Record<string, string>, definition: Record<string, string> }> = {
    "Users": {
      title: { en: "Users", vi: "Người dùng" },
      definition: {
        en: "The total number of unique visitors who have engaged with your website.",
        vi: "Tổng số khách truy cập duy nhất đã tương tác với trang web của bạn."
      }
    },
    "Sessions": {
      title: { en: "Sessions", vi: "Phiên truy cập" },
      definition: {
        en: "A continuous period of user activity. A new session begins after 30 minutes of inactivity.",
        vi: "Khoảng thời gian hoạt động liên tục của người dùng. Phiên mới bắt đầu sau 30 phút không hoạt động."
      }
    },
    "Avg. Duration": {
      title: { en: "Avg. Session Duration", vi: "T.g phiên trung bình" },
      definition: {
        en: "The average amount of time users spend actively engaging with your website per session.",
        vi: "Thời gian trung bình người dùng dành để tương tác tích cực với trang web của bạn trong mỗi phiên."
      }
    },
    "Avg. Session Duration": {
      title: { en: "Avg. Session Duration", vi: "T.g phiên trung bình" },
      definition: {
        en: "The average amount of time users spend actively engaging with your website per session.",
        vi: "Thời gian trung bình người dùng dành để tương tác tích cực với trang web của bạn trong mỗi phiên."
      }
    },
    "Pages/Session": {
      title: { en: "Pages/Session", vi: "Trang/Phiên" },
      definition: {
        en: "The average number of pages viewed during a session. Repeated views of a single page are counted.",
        vi: "Số trang trung bình được xem trong một phiên. Các lần xem lại của một trang cũng được tính."
      }
    },
    "Bounce Rate": {
      title: { en: "Bounce Rate", vi: "Tỷ lệ thoát" },
      definition: {
        en: "The percentage of sessions where the user leaves without interacting with the page.",
        vi: "Tỷ lệ phần trăm các phiên mà người dùng rời đi mà không tương tác với trang."
      }
    },
    "Clicks Tracking": {
      title: { en: "Clicks Tracking", vi: "Theo dõi lượt nhấp" },
      definition: {
        en: "A chronological view of the number of clicks/visits received over the selected time period.",
        vi: "Chế độ xem theo thời gian về số lượng lần nhấp/truy cập trong khoảng thời gian đã chọn."
      }
    },
    "Page Views": {
      title: { en: "Page Views", vi: "Lượt xem trang" },
      definition: {
        en: "The total number of pages viewed. Repeated views of a single page are counted.",
        vi: "Tổng số trang đã xem. Các lần xem lại của một trang cũng được tính."
      }
    },
    "Where Customers From": {
      title: { en: "Where Customers From", vi: "Nguồn khách hàng" },
      definition: {
        en: "The origin of your traffic (e.g., Direct, Social Media, Search Engines).",
        vi: "Nguồn gốc lưu lượng truy cập của bạn (ví dụ: Trực tiếp, Mạng xã hội, Công cụ tìm kiếm)."
      }
    },
    "Time Tracking": {
      title: { en: "Time Tracking", vi: "Theo dõi thời gian" },
      definition: {
        en: "A heatmap showing peak traffic times by hour of the day and day of the week.",
        vi: "Bản đồ nhiệt hiển thị thời gian lưu lượng truy cập cao điểm theo giờ trong ngày và ngày trong tuần."
      }
    },
    "User By Device": {
      title: { en: "User By Device", vi: "Thiết bị người dùng" },
      definition: {
        en: "Breakdown of visitors by the type of device used (Desktop, Mobile, Tablet).",
        vi: "Phân loại khách truy cập theo loại thiết bị sử dụng (Máy tính để bàn, Di động, Máy tính bảng)."
      }
    },
    "Visits By City": {
      title: { en: "Visits By City", vi: "Truy cập theo thành phố" },
      definition: {
        en: "Geographic distribution of your traffic sources by city.",
        vi: "Phân bố địa lý của các nguồn lưu lượng truy cập theo thành phố."
      }
    },
    "Top Browsing Pages Per Minute": {
      title: { en: "Top Browsing Pages Per Minute", vi: "Các trang hàng đầu mỗi phút" },
      definition: {
        en: "A real-time snapshot of which pages are currently receiving the most traffic.",
        vi: "Ảnh chụp nhanh theo thời gian thực về các trang hiện đang nhận được nhiều lưu lượng truy cập nhất."
      }
    },
    "Page": {
      title: { en: "Page", vi: "Trang" },
      definition: {
        en: "The specific URL path of the page being visited.",
        vi: "Đường dẫn URL cụ thể của trang đang được truy cập."
      }
    },
    "Views": {
      title: { en: "Views", vi: "Lượt xem" },
      definition: {
        en: "The number of times this specific page has been viewed in the selected period.",
        vi: "Số lần trang cụ thể này đã được xem trong khoảng thời gian đã chọn."
      }
    },
    "Bounce Rate Table": {
      title: { en: "Bounce Rate", vi: "Tỷ lệ thoát" },
      definition: {
        en: "The percentage of visitors who left after viewing only this page.",
        vi: "Tỷ lệ phần trăm khách truy cập đã rời đi sau khi chỉ xem trang này."
      }
    }
  };

  const entry = glossary[term] || glossary[term + " Table"];
  const definition = entry?.definition?.[language] || "No definition available.";
  // Use the localized title from glossary, or fallback to term if not found
  const displayTitle = entry?.title?.[language] || term;

  const positionClass = side === 'top' 
    ? 'bottom-full mb-2 transform translate-y-1 group-hover:translate-y-0' 
    : 'top-full mt-2 transform -translate-y-1 group-hover:translate-y-0';

  const arrowClass = side === 'top'
    ? 'top-full -mt-px border-t-slate-900'
    : 'bottom-full -mb-px border-b-slate-900';

  return (
    <div className="group relative inline-block cursor-help">
      <div className="flex items-center gap-1 border-b border-dashed border-slate-300 dark:border-slate-600 pb-0.5 hover:border-indigo-400 dark:hover:border-indigo-400 transition-colors">
        {children}
      </div>
      
      <div className={`absolute left-1/2 -translate-x-1/2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none ${positionClass}`}>
        <div className="bg-slate-900 text-white text-xs rounded-xl py-3 px-4 shadow-xl relative">
          <div className="font-bold mb-1 text-indigo-300">{displayTitle}</div>
          <div className="text-slate-300 leading-relaxed">{definition}</div>
          {/* Arrow */}
          <div className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${arrowClass}`}></div>
        </div>
      </div>
    </div>
  );
};